import React, { useState, useRef, useEffect } from "react";
import {
  ChevronDown,
  Heart,
  Smile,
  Users,
  BookOpen,
  Stethoscope,
  Link,
  User,
  X,
} from "lucide-react";
import { getAllPersonas } from "../../../../api/apiFunction/personaServices";
import { toast } from "react-toastify";

const PersonasDropdown = ({ onPersonaSelect, selectedPersona }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Default icon mapping for personas (fallback icons)
  const getPersonaIcon = (personaName) => {
    const name = personaName?.toLowerCase() || "";
    if (name.includes("companion")) return Heart;
    if (name.includes("comedian") || name.includes("funny")) return Smile;
    if (name.includes("friend")) return Users;
    if (name.includes("helper") || name.includes("homework")) return BookOpen;
    if (name.includes("trainer") || name.includes("fitness"))
      return Stethoscope;
    if (name.includes("therapist") || name.includes("therapy")) return Link;
    return User; // Default icon
  };

  // Color mapping for personas
  const getPersonaColor = (index) => {
    const colors = [
      "text-pink-400",
      "text-yellow-400",
      "text-orange-400",
      "text-green-400",
      "text-blue-400",
      "text-cyan-400",
      "text-purple-400",
      "text-red-400",
    ];
    return colors[index % colors.length];
  };

  // Fetch personas from backend
  const fetchPersonas = async () => {
    setLoading(true);
    try {
      const response = await getAllPersonas();

      // Handle different response formats
      let personasData = [];
      if (
        response &&
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        personasData = response.data.data;
      } else if (response && response.data && Array.isArray(response.data)) {
        personasData = response.data;
      } else {
        personasData = [];
      }
      setPersonas(personasData);
    } catch (error) {
      toast.error(
        "Failed to load personas: " +
          (error.response?.data?.message || error.message)
      );
      setPersonas([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch personas on component mount - skip for anonymous users
  useEffect(() => {
    // Check if user is anonymous
    let user = null;
    try {
      const userData = localStorage.getItem("user");
      user = userData ? JSON.parse(userData) : null;
    } catch (e) {}

    // Skip fetch for anonymous users (personas are optional)
    if (!user || !user.token) {
      setPersonas([]);
      setLoading(false);
      return;
    }

    fetchPersonas();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePersonaClick = (persona) => {
    onPersonaSelect?.(persona);
    setIsOpen(false);
  };

  // Add function to clear selection
  const handleClearSelection = (e) => {
    e.stopPropagation();
    onPersonaSelect?.(null);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-2 md:px-3 py-1.5 md:py-2 pl-3 md:pl-4 rounded-full text-fg-60 hover:text-fg-50 focus:outline-none text-xs md:text-sm transition-all bg-bg-50 touch-manipulation ${
          isOpen ? "text-fg-50" : ""
        }`}
        disabled={loading}
        title={
          selectedPersona
            ? `Selected: ${selectedPersona.name}`
            : "Select Persona"
        }
      >
        {selectedPersona ? (
          <>
            {(() => {
              const IconComponent = getPersonaIcon(selectedPersona.name);
              const colorIndex = personas.findIndex(
                (p) => p._id === selectedPersona._id
              );
              const color = getPersonaColor(colorIndex >= 0 ? colorIndex : 0);
              return <IconComponent className={`w-4 h-4 ${color}`} />;
            })()}
            <span className="hidden sm:inline">{selectedPersona.name}</span>
            <X
              className="w-3 h-3 text-fg-60 hover:text-fg-50 transition-colors"
              onClick={handleClearSelection}
            />
          </>
        ) : loading ? (
          <>
            <span className="hidden sm:inline">Loading...</span>
          </>
        ) : (
          <>
            <span className="hidden sm:inline">Personas</span>
            <ChevronDown
              className={`w-3 h-3 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
              strokeWidth={1.5}
            />
          </>
        )}
      </button>

      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      {isOpen && (
        <div className="fixed md:absolute top-auto md:top-full bottom-0 md:bottom-auto left-0 right-0 md:left-0 md:right-auto md:mt-2 bg-bg-60 rounded-t-2xl md:rounded-2xl shadow-md border border-bd-50 z-[60] overflow-hidden animate-slide-up md:animate-none md:min-w-[200px] max-h-[85vh]">
          {/* Mobile header with close button */}
          <div className="flex items-center justify-between p-4 pb-2 md:hidden border-b border-bd-50">
            <h3 className="text-sm font-medium text-fg-50">Select Persona</h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg text-fg-60 hover:text-fg-50 hover:bg-bg-50 transition-colors touch-manipulation"
              aria-label="Close"
            >
              <X size={20} strokeWidth={2} />
            </button>
          </div>
          <div className="py-2 max-h-[60vh] md:max-h-none overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="px-4 py-3 text-sm text-fg-60">
                Loading personas...
              </div>
            ) : personas.length === 0 ? (
              <div className="px-4 py-3 text-sm text-fg-60">
                No personas available
              </div>
            ) : (
              <>
                {personas.map((persona, index) => {
                  const IconComponent = getPersonaIcon(persona.name);
                  const color = getPersonaColor(index);
                  const isSelected =
                    selectedPersona && selectedPersona._id === persona._id;
                  return (
                    <button
                      key={persona._id}
                      onClick={() => handlePersonaClick(persona)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-40 transition-colors touch-manipulation ${
                        isSelected ? "bg-bg-40" : ""
                      }`}
                    >
                      <IconComponent className={`w-5 h-5 ${color}`} />
                      <div className="text-sm md:text-xs font-normal text-fg-50">
                        {persona.name}
                      </div>
                      {isSelected && (
                        <div className="ml-auto text-fg-50 text-lg">✓</div>
                      )}
                    </button>
                  );
                })}
                {/* Add "None" option at the bottom */}
                <button
                  onClick={() => handlePersonaClick(null)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-40 transition-colors border-t border-bd-50 touch-manipulation"
                >
                  <div className="w-5 h-5" />{" "}
                  {/* Empty space for icon alignment */}
                  <div className="text-sm md:text-xs font-normal text-fg-50">
                    None
                  </div>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonasDropdown;
